import type { Story } from '@/content/stories'
import { story } from '@/content/storyBuilder'

// Asked for on 2026-09-22 — "1st CTA Read the Story butonu olsun bu linke
// yönlendirsin", with this address. The header already renders "Read the Story"
// as its primary button whenever the story has a `download`, so the only thing
// missing was the document. It is not set through the builder's `doc` option,
// which prefixes the bucket's `success-story/` folder: this file was uploaded
// under `report/`, so the whole URL is written here, with its spaces encoded
// the way `pdf()` does for the other stories.
const DOCUMENT
  = 'https://njdxtkezxgkrmwwhzcne.supabase.co/storage/v1/object/public/website-documents'
  + '/report/c7c952ac-cc77-4243-999a-f6e25fdab322-Zorlu%20Holding%20and%20Digitopia.pdf'

// Written from the copy supplied on 2026-09-21 — "Zorlu Holding Success Story
// olacak şekilde ... zorlu holding success story sayfasını yapalım" — together
// with five photographs (Vestel City's entrance, a white-goods assembly line, an
// aerial of the plant, and the two principals' portraits) and Zorlu Holding's
// mark for the card.
//
// The prose arrived as one continuous piece with the two executive quotes
// separated off at the foot under "Executive quotes". So the quotes are
// `content/testimonials/` files, which is where the template draws the closing
// card from, and the prose is headed into the template's own sections: the
// opening line becomes the standfirst quotation, the paragraph under it the
// grey intro band, and the four decision questions become a list because they
// are a list in the source.
//
// Asked on 2026-09-21 to sit in the same design as the other success stories.
// This page had `centred: false` on every section, which ranged its panels left
// while the template's own default — grey panel, centred single column — is what
// the other written stories render in, so it read as a different page. The
// overrides are gone, and its two in-story photographs now sit beside the words
// (right, then left) the way Temsa's and Penta Teknoloji's do instead of running
// full width under them.
//
// No figures were supplied and none has been invented: this page makes no claim
// about scores, timing or savings. The two things it does state that are not
// this site's to state — the Ministry programme application and the extension
// to other Zorlu Holding businesses — are both forward-looking, so the check
// below asks for them to be confirmed before production.
const zorluHolding = story('zorlu-holding', 'Zorlu Holding', {
  // Asked for on 2026-09-22 — the page headline should read just "Zorlu
  // Holding" rather than the full success-story title. No `headline` means
  // Stories.tsx falls back to `story.name`, which is already "Zorlu Holding".
  lede: ['“Digitopia’s approach made the differences between plants visible.”'],
  intro:
    'Zorlu Holding measured Vestel’s digital and AI maturity **plant by plant**. Instead of one '
    + 'company score, it gained a shared starting point that shows where each site should focus '
    + 'first.',
  // Asked for on 2026-09-21 — "bu hero image olsun", with the Vestel City
  // entrance attached. It was the page's `photo` (the wide picture under the
  // standfirst) and is now its `heroPhoto`, which is the slot beside the
  // headline; the header showed Zorlu Holding's mark before. It is not in both
  // places, because the same photograph twice on one page reads as a mistake —
  // and the copy attached was 1280px wide where the file already here is
  // 1536px, so the sharper one is kept.
  heroPhoto: {
    src: '/photo/vestel-city-entrance.jpg',
    alt: 'The entrance to Vestel City under a pale sky, the VESTEL CITY sign above the gatehouse '
      + 'and a red Vestel lorry pulling away from it.',
  },
  sections: [
    {
      heading: 'About Vestel',
      body: [
        'Vestel is **Zorlu Holding’s largest business**. Its manufacturing spans televisions and a '
        + 'broad range of household appliances, at a scale where efficiency and cost shape '
        + 'day-to-day decisions.',
      ],
      photo: {
        src: '/photo/vestel-plant-aerial.png',
        alt: 'An aerial view of a Vestel plant, its long white roofline carrying the Vestel name, '
          + 'with landscaped grounds in front and hills behind.',
      },
      photoSide: 'right',
      // Asked for on 2026-09-21 — "bu fotoğraf kutucuk dışında olsun", with the
      // aerial attached. `photoInPanel` had the grey panel enclosing both
      // columns; without it the panel wraps only the words and the photograph
      // stands beside it, clear of the box.
    },
    {
      heading: 'One score could not show where to act',
      body: [
        'That kind of operation can be summarised in a single maturity score. The number may be '
        + 'useful at group level, but it cannot show leaders **where to act inside each factory**.',
        '**Vestel is not one production environment.** Each plant has its own lines, constraints, '
        + 'investment history and established ways of working. A strength at one site can easily '
        + 'disappear into the same average as a development need at another.',
      ],
    },
    {
      heading: 'Every plant measured with the same method',
      body: [
        'Digitopia therefore assessed the electronics operations and household appliance plants '
        + 'separately using **DAIMI, the Digital and AI Maturity Index**. Every site was measured '
        + 'with **the same method** and set against the same external comparisons.',
        'The result was a common starting point that put the plants side by side without losing '
        + 'their differences.',
      ],
      photo: {
        src: '/photo/vestel-appliance-assembly-line.jpg',
        alt: 'Two long washing-machine assembly lines running away from the camera inside a '
          + 'Vestel plant, operators in red shirts working at stations along both sides.',
      },
      photoSide: 'left',
    },
    {
      heading: 'The decisions that view supports',
      body: ['That view supports more practical decisions.'],
      items: [
        { text: 'Which site has a strong approach worth studying?' },
        { text: 'Where is the clearest development need?' },
        { text: 'Which proven practices could be adapted elsewhere?' },
        { text: 'Where should investment begin?' },
      ],
    },
    {
      heading: 'An independent starting point, measured from outside',
      body: [
        'Önder Kaplancık, General Manager of Technology and AI at Zorlu Holding, leads the '
        + 'group’s digital and AI transformation. He wanted the assessment to come from outside '
        + 'the business and to be grounded in external comparisons. Vestel now has **an independent '
        + 'starting point** it can use to track progress over time.',
      ],
    },
    {
      heading: 'A roadmap ordered around where cost is created',
      body: [
        'For Melih Sarıkaya, CIO of Vestel, the next important output was the roadmap. Priorities '
        + 'were ordered around the areas where **manufacturing cost is created**:',
      ],
      items: [
        { text: 'Energy' },
        { text: 'Scrap' },
        { text: 'Downtime' },
        { text: 'Planning accuracy' },
        { text: 'Labour productivity' },
        { text: 'The cost of poor quality' },
      ],
      after: [
        'This gave the team a clearer basis for deciding what to address first.',
      ],
    },
    {
      heading: 'Support for the investment that follows',
      body: [
        'Vestel also plans to apply to the Digital Transformation Support Program run by '
        + 'Türkiye’s Ministry of Industry and Technology. Digitopia is **an authorized Digital '
        + 'Transformation Assessor** under the program. Subject to application and eligibility '
        + 'requirements, the support could reduce the financing burden of planned investments.',
      ],
    },
    {
      heading: 'What comes next',
      body: [
        'The next step is to extend the same assessment approach to **other Zorlu Holding '
        + 'businesses**, creating a consistent way to track progress across the group.',
      ],
    },
  ],
  check:
    'Three things on this page need confirming with Zorlu Holding before it is published, '
    + 'because they were supplied as copy rather than as evidence: the two executive quotes and '
    + 'both job titles (Önder Kaplancık, General Manager of Technology and AI, Zorlu Holding; '
    + 'Melih Sarıkaya, CIO, Vestel); the sentence about applying to the Ministry of Industry and '
    + 'Technology’s Digital Transformation Support Program, which is stated as a plan and not as '
    + 'something done; and the closing sentence about extending the assessment to other Zorlu '
    + 'Holding businesses. There are also no figures anywhere on it, which is deliberate: none '
    + 'were given and none has been invented.',
})

export const added: Story = { ...zorluHolding, download: DOCUMENT }

// Asked for on 2026-09-22 — "zorlu holding butonu yukarıdak en üstteki ilk
// seçenek olsun". The file carried no `order`, which sorts a story last; a
// negative number puts this card ahead of the hand-placed marketing order, so
// it is the first tile in the grid on /customers.
export const order = -2
